import BigNumber from 'bignumber.js'
import masterchefABI from 'config/abi/masterchef.json'
import erc20 from 'config/abi/erc20.json'
import { getAddress, getMasterChefAddress } from 'utils/addressHelpers'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import { multicallWithAbi } from 'utils/multicall'
import { Farm, SerializedBigNumber } from '../types'

type PublicFarmData = {
  tokenAmountMc: SerializedBigNumber
  quoteTokenAmountMc: SerializedBigNumber
  tokenAmountTotal: SerializedBigNumber
  quoteTokenAmountTotal: SerializedBigNumber
  lpTotalInQuoteToken: SerializedBigNumber
  lpTotalSupply: SerializedBigNumber
  tokenPriceVsQuote: SerializedBigNumber
  poolWeight: SerializedBigNumber
  multiplier: string
}

const fetchFarm = async (farm: Farm): Promise<PublicFarmData> => {
  try {
    const { pid, address, token, quoteToken, isTokenOnly } = farm
    const lpAddress = getAddress(address)
    const calls = [
      // Balance of token in the LP contract
      {
        address: getAddress(token.address),
        name: 'balanceOf',
        params: [lpAddress],
        abi: erc20,
      },
      // Balance of quote token on LP contract
      {
        address: getAddress(quoteToken.address),
        name: 'balanceOf',
        params: [lpAddress],
        abi: erc20,
      },
      // Balance of LP tokens in the master chef contract
      {
        address: isTokenOnly ? getAddress(token.address) : lpAddress,
        name: 'balanceOf',
        params: [getMasterChefAddress()],
        abi: erc20,
      },
      // Total supply of LP tokens
      {
        address: lpAddress,
        name: 'totalSupply',
        abi: erc20,
      },
      // Token decimals
      {
        address: getAddress(token.address),
        name: 'decimals',
        abi: erc20,
      },
      // Quote token decimals
      {
        address: getAddress(quoteToken.address),
        name: 'decimals',
        abi: erc20,
      },
      {
        address: getMasterChefAddress(),
        name: 'poolInfo',
        // Only make masterchef calls if farm has pid
        params: [pid || 0],
        abi: masterchefABI,
      },
      {
        address: getMasterChefAddress(),
        name: 'totalAllocPoint',
        abi: masterchefABI,
      },
    ]
    const [
      tokenBalanceLP,
      quoteTokenBalanceLP,
      lpTokenBalanceMC,
      lpTotalSupply,
      tokenDecimals,
      quoteTokenDecimals,
      info,
      totalAllocPoint,
    ] = await multicallWithAbi(calls)
    let tokenAmountMc
    let quoteTokenAmountMc
    let lpTotalInQuoteToken
    let tokenPriceVsQuote
    let tokenAmountTotal
    let quoteTokenAmountTotal
    if (isTokenOnly) {
      tokenAmountMc = new BigNumber(lpTokenBalanceMC).div(BIG_TEN.pow(tokenDecimals))
      quoteTokenAmountMc = new BigNumber(lpTokenBalanceMC).div(BIG_TEN.pow(quoteTokenDecimals))
      tokenAmountTotal = new BigNumber(tokenBalanceLP).div(BIG_TEN.pow(tokenDecimals))
      quoteTokenAmountTotal = new BigNumber(quoteTokenBalanceLP).div(BIG_TEN.pow(quoteTokenDecimals))
      tokenPriceVsQuote = quoteTokenAmountTotal.div(tokenAmountTotal)
      lpTotalInQuoteToken = tokenAmountMc.times(tokenPriceVsQuote)
    } else {
      // Ratio in % of LP tokens that are staked in the MC, vs the total number in circulation
      const lpTokenRatio = new BigNumber(lpTokenBalanceMC).div(new BigNumber(lpTotalSupply))

      // Raw amount of token in the LP, including those not staked
      tokenAmountTotal = new BigNumber(tokenBalanceLP).div(BIG_TEN.pow(tokenDecimals))
      quoteTokenAmountTotal = new BigNumber(quoteTokenBalanceLP).div(BIG_TEN.pow(quoteTokenDecimals))
      // Amount of token in the LP that are staked in the MC (i.e amount of token * lp ratio)
      tokenAmountMc = tokenAmountTotal.times(lpTokenRatio)
      quoteTokenAmountMc = quoteTokenAmountTotal.times(lpTokenRatio)

      // Total staked in LP, in quote token value
      lpTotalInQuoteToken = quoteTokenAmountMc.times(new BigNumber(2))
      tokenPriceVsQuote = quoteTokenAmountTotal.div(tokenAmountTotal)
    }
    const allocPoint = info ? new BigNumber(info.allocPoint?._hex) : BIG_ZERO
    const poolWeight = totalAllocPoint ? allocPoint.div(new BigNumber(totalAllocPoint)) : BIG_ZERO
    return {
      tokenAmountMc: tokenAmountMc.toJSON(),
      quoteTokenAmountMc: quoteTokenAmountMc.toJSON(),
      tokenAmountTotal: tokenAmountTotal.toJSON(),
      quoteTokenAmountTotal: quoteTokenAmountTotal.toJSON(),
      lpTotalSupply: new BigNumber(lpTotalSupply).toJSON(),
      lpTotalInQuoteToken: lpTotalInQuoteToken.toJSON(),
      tokenPriceVsQuote: tokenPriceVsQuote.toJSON(),
      poolWeight: poolWeight.toJSON(),
      multiplier: `${allocPoint.div(100).toString()}X`,
    }
  } catch (error) {
    console.error(error)
    return null
  }
}

export default fetchFarm
